import { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import { OrderProvisioningSection } from './OrderProvisioningSection';
import { OnePwPanel } from './OnePwPanel';
import { InternalToolsPanel } from './InternalToolsPanel';
import { ResellerPanel } from './ResellerPanel';
import './Orders.css';

/* ─── Types ──────────────────────────────────────────────── */

type OrderStatus =
  | 'configuring'
  | 'availability_checked'
  | 'purchasing'
  | 'purchased'
  | 'failed'
  | 'cancelled';

type DomainStatus = 'pending' | 'available' | 'unavailable' | 'premium' | 'error';

interface Order {
  id: string;
  status: OrderStatus;
  panel_count: number;
  capacity: number;
  domain_base: string;
  domain_tld: string;
  starting_reseller_id: string;
  starting_domain_number: number;
  notes: string | null;
  triggered_by: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  availability_checked_at: string | null;
  purchased_at: string | null;
  // Provisioning-verify orchestrator state. See OrderProvisioningSection.
  provisioning_status:
    | 'idle' | 'polling_first' | 'verifying_all' | 'configuring' | 'done'
    | 'failed_first_timeout' | 'failed_partial';
  provisioning_polling_active: boolean;
  provisioning_started_at: string | null;
  provisioning_attempts: number;
  provisioning_attempt_at: string | null;
  first_panel_ready_at: string | null;
  all_panels_ready_at: string | null;
  // Aggregates only present on the list endpoint (GET /api/orders); detail
  // endpoint (GET /api/orders/:id) returns panels[] for richer state instead.
  panels_total?: number;
  panels_done?: number;
  panels_failed?: number;
  panels_inflight?: number;
}

interface OrderDomain {
  id: string;
  order_id: string;
  position: number;
  reseller_id: string;
  domain: string;
  availability_status: DomainStatus;
  availability_message: string | null;
  availability_checked_at: string | null;
  purchase_status: 'pending' | 'purchasing' | 'purchased' | 'failed' | null;
  purchase_message: string | null;
  purchased_at: string | null;
  cloudflare_account_id: number | null;
  cloudflare_zone_id: string | null;
  nameservers: string[] | null;
  nameservers_set_at: string | null;
  provisioned_at: string | null;
}

interface PanelConfigState {
  id: string;
  email: string;
  reseller_id: string | null;
  capacity: number;
  organization_name: string | null;
  organization_description: string | null;
  reseller_login_ok_at: string | null;
  domain_verified_at: string | null;
  gmail_active_at: string | null;
  twofa_enabled_at: string | null;
  dwd_authorized_at: string | null;
  cloud_identity_added_at: string | null;
  auto_licensing_off_at: string | null;
  admin_license_removed_at: string | null;
  profile_picture_editing_at: string | null;
  oauth_apps_trusted_at: string | null;
  org_renamed_at: string | null;
  password_rotated_at: string | null;
  pushed_to_1p_at: string | null;
  pushed_to_internal_tools_at: string | null;
  pushed_to_reseller_at: string | null;
  config_error: string | null;
  health_checked_at: string | null;
  health_dwd_ok: boolean | null;
  health_license_cleared: boolean | null;
  health_workspace_license_count: number | null;
  health_error: string | null;
  provisioned_at: string | null;
  updated_at: string;
}

interface OrderDetail {
  order: Order;
  domains: OrderDomain[];
  panels: PanelConfigState[];
}

const PANEL_PHASES = [
  'reseller_login_ok_at',
  'domain_verified_at',
  'gmail_active_at',
  'twofa_enabled_at',
  'dwd_authorized_at',
  'cloud_identity_added_at',
  'auto_licensing_off_at',
  'admin_license_removed_at',
  'profile_picture_editing_at',
  'oauth_apps_trusted_at',
  'org_renamed_at',
  'password_rotated_at',
] as const;

type PanelPhase = (typeof PANEL_PHASES)[number];

interface PanelStatus {
  state: 'not-started' | 'queued' | 'running' | 'done' | 'failed';
  completedPhases: number;
  totalPhases: number;
  /** First phase whose timestamp is null (the "next up" or stuck phase). */
  nextPhase: PanelPhase | null;
}

function panelStatus(p: PanelConfigState | undefined): PanelStatus {
  if (!p) {
    return { state: 'not-started', completedPhases: 0, totalPhases: PANEL_PHASES.length, nextPhase: PANEL_PHASES[0] };
  }
  const completed = PANEL_PHASES.filter((ph) => p[ph] !== null).length;
  const nextPhase = PANEL_PHASES.find((ph) => p[ph] === null) ?? null;
  if (p.config_error) return { state: 'failed', completedPhases: completed, totalPhases: PANEL_PHASES.length, nextPhase };
  if (completed === PANEL_PHASES.length) return { state: 'done', completedPhases: completed, totalPhases: PANEL_PHASES.length, nextPhase: null };
  if (completed === 0) return { state: 'queued', completedPhases: 0, totalPhases: PANEL_PHASES.length, nextPhase };
  return { state: 'running', completedPhases: completed, totalPhases: PANEL_PHASES.length, nextPhase };
}

function phaseLabel(ph: PanelPhase): string {
  switch (ph) {
    case 'reseller_login_ok_at': return 'Login + speedbumps';
    case 'domain_verified_at': return 'Domain verify';
    case 'gmail_active_at': return 'Gmail active';
    case 'twofa_enabled_at': return '2FA setup';
    case 'dwd_authorized_at': return 'DWD authorize';
    case 'cloud_identity_added_at': return 'Cloud Identity Free';
    case 'auto_licensing_off_at': return 'Auto-licensing off';
    case 'admin_license_removed_at': return 'License removed';
    case 'profile_picture_editing_at': return 'Profile picture';
    case 'oauth_apps_trusted_at': return 'OAuth apps trusted';
    case 'org_renamed_at': return 'Org renamed';
    case 'password_rotated_at': return 'Password rotated';
  }
}

/**
 * Reference table for the panel-config pipeline. Used by the "Pipeline reference"
 * modal so the operator never has to dig into code to remember what each stage does.
 */
const PHASE_REFERENCE: { num: number; key: PanelPhase | PanelPhase[]; name: string; what: string }[] = [
  {
    num: 1,
    key: 'reseller_login_ok_at',
    name: 'Login + reseller speedbumps',
    what: 'Logs in to admin.google.com with the reseller-set temp password (no 2FA yet) and clicks through the two first-login interstitials: "Welcome to your new account" → "I understand", and the Workspace ToS → "Accept Terms of Service".',
  },
  {
    num: 2,
    key: ['domain_verified_at', 'gmail_active_at'],
    name: 'Domain verify + Gmail active',
    what: 'On /ac/domains/manage, clicks Verify on the primary admin domain. Writes the google-site-verification TXT and the smtp.google.com MX record to Cloudflare via API, then confirms — Google flips the row to "Verified · Gmail activated".',
  },
  {
    num: 3,
    key: 'twofa_enabled_at',
    name: '2FA setup (Authenticator)',
    what: 'On myaccount.google.com/signinoptions, sets up Google Authenticator: extracts the 8-quartet TOTP secret, verifies a generated code, then enables 2-Step Verification (with the reload-then-click workaround for the phone-number speedbump). Saves the secret encrypted on the panel.',
  },
  {
    num: 4,
    key: 'dwd_authorized_at',
    name: 'DWD authorization',
    what: 'On /ac/owl/domainwidedelegation, authorizes the project service account (client ID 108109229613227145095) with 5 scopes: admin.directory.user, admin.directory.domain, admin.directory.group, gmail.modify, apps.licensing.',
  },
  {
    num: 5,
    key: 'cloud_identity_added_at',
    name: 'Cloud Identity Free subscription',
    what: 'In the billing catalog, picks the Cloud Identity Free SKU (GOOGLE.IDENTITY_BASIC) and walks the 3-step checkout (Next → Next → Place order). The Free plan needs no terms checkbox.',
  },
  {
    num: 6,
    key: 'auto_licensing_off_at',
    name: 'Auto-licensing OFF',
    what: 'On /ac/billing/licensesettings, opens the Workspace Business Starter Edit panel and switches "Automatic licensing" from ON to OFF. Without this, removing admin@\'s license in the next phase would cause it to immediately re-attach.',
  },
  {
    num: 7,
    key: 'admin_license_removed_at',
    name: 'Workspace license removed (API)',
    what: 'Calls Admin SDK licenseAssignments.delete with DWD impersonation as admin@. Polls licenseAssignments.listForProduct until admin@ no longer appears (with a re-delete fallback for slow propagation). Now admin@ runs on Cloud Identity Free only.',
  },
  {
    num: 8,
    key: 'profile_picture_editing_at',
    name: 'Profile-picture editing',
    what: 'On /ac/directory/profileediting, ticks the "Profile picture · Allow users to change their profile pictures" checkbox so end-users can set photos and have them surface to everyone in the org.',
  },
  {
    num: 9,
    key: 'oauth_apps_trusted_at',
    name: 'OAuth apps trusted (×3)',
    what: 'On /ac/owl/list?tab=configuredApps, walks the "Configure new app" wizard for Instantly, Smartlead, and PlusVibe — searching by client ID, scoping to all users, marking access as Trusted. End-users can grant these apps without warnings.',
  },
  {
    num: 10,
    key: 'org_renamed_at',
    name: 'Organization rename',
    what: 'Renames the root OU on /ac/orgunits AND the customer profile name on /ac/accountsettings/profile to a randomized "XX - YYYYYYYY" identifier (2 capital letters, " - ", 8 alphanumeric uppercase). Replaces the default <domain-prefix> name everywhere it surfaces.',
  },
  {
    num: 11,
    key: 'password_rotated_at',
    name: 'Admin password rotation (API)',
    what: 'Generates a strong 20-char password (letter + must-have digit/symbol/letter, rest mixed) and PUTs it via Admin SDK directory.users.update. Replaces the reseller-set temp password — only we know the new value, encrypted at rest.',
  },
];

/** Derived overall status for an order — combines order.status with panel config state. */
type DerivedStatus =
  | OrderStatus
  | 'config_running'
  | 'config_done'
  | 'config_failed';

function derivedOrderStatus(order: Order, panels: PanelConfigState[], totalDomains: number): DerivedStatus {
  if (order.status !== 'purchased') return order.status;
  if (panels.length === 0) return 'purchased';
  const states = panels.map((p) => panelStatus(p).state);
  const completed = states.filter((s) => s === 'done').length;
  const failed = states.filter((s) => s === 'failed').length;
  const inflight = states.some((s) => s === 'queued' || s === 'running');
  if (failed > 0) return 'config_failed';
  if (inflight) return 'config_running';
  if (completed === totalDomains) return 'config_done';
  return 'purchased';
}

/** List-view variant — driven by aggregate counts the list endpoint returns
 *  (panels_total/done/failed/inflight), so we don't need per-order panel arrays.
 *  Mirrors the detail-view logic so badges stay consistent across views. */
function derivedListStatus(o: Order): DerivedStatus {
  if (o.status !== 'purchased') return o.status;
  const total = o.panels_total ?? 0;
  if (total === 0) return 'purchased';
  if ((o.panels_failed ?? 0) > 0) return 'config_failed';
  if ((o.panels_inflight ?? 0) > 0) return 'config_running';
  if ((o.panels_done ?? 0) === o.panel_count) return 'config_done';
  return 'purchased';
}

function derivedStatusLabel(s: DerivedStatus): string {
  switch (s) {
    case 'config_done': return 'config done';
    case 'config_running': return 'configuring';
    case 'config_failed': return 'config failed';
    default: return s.replace(/_/g, ' ');
  }
}

interface BaseSuggestion {
  base: string;
  tld: string;
  highest_number: number;
  count: number;
}

interface Suggestions {
  bases: BaseSuggestion[];
  nextResellerId: string | null;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

/* ─── Icons ──────────────────────────────────────────────── */

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const XIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/* ─── Main component ─────────────────────────────────────── */

export function Orders(): JSX.Element {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await api<Order[]>('/api/orders');
      setOrders(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load orders');
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreated = async (newOrderId: string): Promise<void> => {
    setShowCreate(false);
    showToast('success', 'Order created');
    await refresh();
    setSelectedId(newOrderId);
  };

  if (selectedId !== null) {
    return (
      <OrderDetailView
        orderId={selectedId}
        onBack={() => {
          setSelectedId(null);
          void refresh();
        }}
        showToast={showToast}
        toast={toast}
      />
    );
  }

  return (
    <div className="ord-root">
      <div className="ord-scroll">
        <header className="ord-header">
          <div className="ord-header-row">
            <div>
              <h1 className="ord-title">Orders</h1>
              <p className="ord-subtitle">
                Plan a batch of admin panels — choose IDs and domains, check availability on Spaceship,
                then purchase.
              </p>
            </div>
            <button
              type="button"
              className="ord-btn ord-btn--primary"
              onClick={() => setShowCreate(true)}
            >
              <PlusIcon />
              New order
            </button>
          </div>
        </header>

        <section className="ord-card">
          <div className="ord-card-header">
            <h2 className="ord-card-title">Orders ({orders?.length ?? 0})</h2>
            <button type="button" className="ord-icon-btn" onClick={() => void refresh()} title="Refresh">
              <RefreshIcon />
            </button>
          </div>

          {orders === null && <div className="ord-empty">Loading…</div>}
          {orders !== null && orders.length === 0 && (
            <div className="ord-empty">
              <div className="ord-empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4" />
                  <path d="M9 11V7a3 3 0 0 1 6 0v4" />
                </svg>
              </div>
              <h3 className="ord-empty-title">No orders yet</h3>
              <p className="ord-empty-text">Click <strong>New order</strong> to plan your first batch.</p>
            </div>
          )}

          {orders !== null && orders.length > 0 && (
            <table className="ord-table">
              <thead>
                <tr>
                  <th>Pattern</th>
                  <th>Status</th>
                  <th className="ord-col-num">Panels</th>
                  <th>Range</th>
                  <th>Triggered by</th>
                  <th className="ord-col-date">Created</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const lastNum = o.starting_domain_number + o.panel_count - 1;
                  const range = `${o.domain_base}${o.starting_domain_number}…${lastNum}${o.domain_tld}`;
                  const m = o.starting_reseller_id.match(/^(.*?)(\d+)$/);
                  const idRange = m
                    ? `${m[1]}${m[2]}…${m[1]}${Number.parseInt(m[2] ?? '0', 10) + o.panel_count - 1}`
                    : o.starting_reseller_id;
                  const listStatus = derivedListStatus(o);
                  return (
                    <tr key={o.id} className="ord-row" onClick={() => setSelectedId(o.id)}>
                      <td className="ord-cell-mono">
                        {o.domain_base}*{o.domain_tld}
                      </td>
                      <td>
                        <span className={`ord-badge ord-badge--${listStatus}`}>{derivedStatusLabel(listStatus)}</span>
                      </td>
                      <td className="ord-col-num">{o.panel_count}</td>
                      <td className="ord-cell-mono ord-cell-faint">
                        {idRange} · {range}
                      </td>
                      <td className="ord-cell-mono ord-cell-faint">{o.triggered_by ?? '—'}</td>
                      <td className="ord-col-date ord-cell-faint">{relativeTime(o.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {showCreate && (
        <CreateOrderModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {toast && <div className={`ord-toast ord-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Create modal ───────────────────────────────────────── */

function CreateOrderModal({
  onClose,
  onCreated,
  showToastError,
}: {
  onClose: () => void;
  onCreated: (newOrderId: string) => void;
  showToastError: (msg: string) => void;
}): JSX.Element {
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [base, setBase] = useState('');
  const [tld, setTld] = useState('');
  const [count, setCount] = useState(10);
  const [capacity, setCapacity] = useState(18);
  const [resellerStart, setResellerStart] = useState('');
  const [domainStart, setDomainStart] = useState(1);
  const [notes, setNotes] = useState('');
  const [disableItAutoPush, setDisableItAutoPush] = useState(false);

  const baseInputRef = useRef<HTMLInputElement>(null);

  // Load suggestions, pre-fill from the most-used base
  useEffect(() => {
    void api<Suggestions>('/api/orders/suggestions').then((s) => {
      setSuggestions(s);
      const top = s.bases[0];
      if (top) {
        setBase(top.base);
        setTld(top.tld);
        setDomainStart(top.highest_number + 1);
      } else {
        baseInputRef.current?.focus();
      }
      if (s.nextResellerId) setResellerStart(s.nextResellerId);
    });
  }, []);

  // When base/tld change, recompute the suggested starting domain number.
  // If the combo is brand-new (no panels for this base+tld), reset to 1.
  useEffect(() => {
    if (!suggestions || !base || !tld) return;
    const match = suggestions.bases.find(
      (b) => b.base === base && b.tld === (tld.startsWith('.') ? tld : `.${tld}`),
    );
    setDomainStart(match ? match.highest_number + 1 : 1);
  }, [base, tld, suggestions]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await api<{ order: Order }>('/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          panel_count: count,
          capacity,
          domain_base: base.trim().toLowerCase(),
          domain_tld: tld.trim().toLowerCase(),
          starting_reseller_id: resellerStart.trim(),
          starting_domain_number: domainStart,
          notes: notes.trim() || null,
          disable_internal_tools_auto_push: disableItAutoPush,
        }),
      });
      onCreated(result.order.id);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to create order';
      showToastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Preview the first/last row
  const m = resellerStart.match(/^(.*?)(\d+)$/);
  const lastResellerId = m
    ? `${m[1]}${Number.parseInt(m[2] ?? '0', 10) + count - 1}`
    : '?';
  const firstDomain = base && tld ? `${base}${domainStart}${tld.startsWith('.') ? tld : '.' + tld}` : '—';
  const lastDomain = base && tld
    ? `${base}${domainStart + count - 1}${tld.startsWith('.') ? tld : '.' + tld}`
    : '—';

  return (
    <div className="ord-modal-overlay" onClick={onClose}>
      <div className="ord-modal ord-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="ord-modal-header">
          <h3 className="ord-modal-title">New order</h3>
          <button type="button" className="ord-icon-btn" onClick={onClose} title="Close">
            <XIcon />
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="ord-modal-body">
            <div className="ord-form-grid">
              <div className="ord-field">
                <label className="ord-label">Admin panel ID</label>
                <input
                  className="ord-input ord-input-mono"
                  value={resellerStart}
                  onChange={(e) => setResellerStart(e.target.value)}
                  placeholder="S4047"
                  required
                />
                <p className="ord-hint">{suggestions?.nextResellerId ? `Suggested: ${suggestions.nextResellerId}` : 'No existing IDs to suggest from'}</p>
              </div>

              <div className="ord-field">
                <label className="ord-label">Domain base</label>
                <input
                  ref={baseInputRef}
                  className="ord-input"
                  list="ord-bases-datalist"
                  value={base}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBase(v);
                    const match = suggestions?.bases.find((b) => b.base === v);
                    if (match) setTld(match.tld);
                  }}
                  placeholder="omnimedia"
                  pattern="[a-z][a-z0-9\-]*"
                  required
                />
                <datalist id="ord-bases-datalist">
                  {suggestions?.bases.map((b) => (
                    <option key={`${b.base}|${b.tld}`} value={b.base}>
                      {b.tld} · {b.count} existing · last {b.highest_number}
                    </option>
                  ))}
                </datalist>
                <p className="ord-hint">Pick from existing or type a new base. Used as the prefix in <span className="ord-mono">basename1234.shop</span>.</p>
              </div>

              <div className="ord-field">
                <label className="ord-label">Domain postfix</label>
                <input
                  className="ord-input ord-input-mono"
                  type="number"
                  min={1}
                  value={domainStart}
                  onChange={(e) => setDomainStart(Math.max(1, Number.parseInt(e.target.value || '1', 10)))}
                  required
                />
              </div>

              <div className="ord-field">
                <label className="ord-label">TLD</label>
                <input
                  className="ord-input ord-input-mono"
                  value={tld}
                  onChange={(e) => setTld(e.target.value)}
                  placeholder=".shop"
                  pattern="\.?[a-z]{2,12}"
                  required
                />
              </div>

              <div className="ord-field">
                <label className="ord-label">Number of panels</label>
                <input
                  className="ord-input"
                  type="number"
                  min={1}
                  max={200}
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(200, Number.parseInt(e.target.value || '1', 10))))}
                  required
                />
              </div>

              <div className="ord-field">
                <label className="ord-label">Capacity</label>
                <input
                  className="ord-input"
                  type="number"
                  min={0}
                  max={100000}
                  value={capacity}
                  onChange={(e) => setCapacity(Math.max(0, Math.min(100000, Number.parseInt(e.target.value || '0', 10))))}
                  required
                />
                <p className="ord-hint">Same value applied to every panel in this batch.</p>
              </div>

              <div className="ord-field ord-field--wide">
                <label className="ord-label">Notes (optional)</label>
                <input
                  className="ord-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Q2 2026 batch for Ravi"
                />
              </div>

              <div className="ord-field ord-field--wide">
                <label className="ord-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={disableItAutoPush}
                    onChange={(e) => setDisableItAutoPush(e.target.checked)}
                  />
                  <span>Skip auto-push to Internal Tools</span>
                </label>
                <p className="ord-hint">When checked, panels won't auto-push to Internal Tools when configured. Manual bulk-push button on the order page still works.</p>
              </div>
            </div>

            <div className="ord-preview">
              <div className="ord-preview-label">Preview</div>
              <div className="ord-preview-rows">
                <div className="ord-preview-row">
                  <span className="ord-mono">{resellerStart || '—'}</span>
                  <span className="ord-mono ord-cell-faint">{firstDomain}</span>
                </div>
                {count > 2 && (
                  <div className="ord-preview-row ord-preview-row--ellipsis">
                    <span>… {count - 2} more rows …</span>
                  </div>
                )}
                {count > 1 && (
                  <div className="ord-preview-row">
                    <span className="ord-mono">{lastResellerId}</span>
                    <span className="ord-mono ord-cell-faint">{lastDomain}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="ord-modal-footer">
            <button type="button" className="ord-btn ord-btn--ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="ord-btn ord-btn--primary" disabled={submitting}>
              {submitting ? 'Creating…' : `Create order (${count} panels)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Detail view ────────────────────────────────────────── */

function OrderDetailView({
  orderId,
  onBack,
  showToast,
  toast,
}: {
  orderId: string;
  onBack: () => void;
  showToast: (type: 'success' | 'error', message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [checking, setChecking] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [checkingStatuses, setCheckingStatuses] = useState(false);
  const [pushingPanelId, setPushingPanelId] = useState<string | null>(null);
  const [pushingITPanelId, setPushingITPanelId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showPhaseRef, setShowPhaseRef] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await api<OrderDetail>(`/api/orders/${orderId}`);
      setDetail(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load order');
    }
  }, [orderId, showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll the detail view while the order is in-flight so the UI stays live —
  // covers BOTH the purchasing pipeline AND any panel configuration in flight.
  const detailPollRef = useRef<number | null>(null);
  useEffect(() => {
    const purchasing = detail?.order.status === 'purchasing';
    const anyPanelRunning = (detail?.panels ?? []).some((p) => {
      const s = panelStatus(p);
      return s.state === 'running' || s.state === 'queued';
    });
    const inflight = purchasing || anyPanelRunning;
    if (!inflight) {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
      return;
    }
    if (detailPollRef.current === null) {
      detailPollRef.current = window.setInterval(() => {
        void refresh();
      }, 5000);
    }
    return () => {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [detail, refresh]);

  const checkAvailability = async (): Promise<void> => {
    setChecking(true);
    try {
      await api<{ checked: number; domains: OrderDomain[] }>(`/api/orders/${orderId}/check-availability`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      showToast('success', 'Availability check complete');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Check failed';
      showToast('error', msg);
    } finally {
      setChecking(false);
    }
  };

  const purchase = async (): Promise<void> => {
    if (!detail) return;
    const purchasable = detail.domains.filter(
      (d) => (d.availability_status === 'available' || d.availability_status === 'premium') &&
             (!d.purchased_at || !d.cloudflare_zone_id || !d.nameservers_set_at || d.purchase_status !== 'purchased'),
    ).length;
    if (purchasable === 0) {
      showToast('error', 'No rows are available for purchase. Run availability check first.');
      return;
    }
    if (!confirm(
      `This will complete setup for ${purchasable} domain(s): purchase any not yet bought, ` +
      `create missing Cloudflare zones, and finish nameserver delegation. Continue?`
    )) return;

    setPurchasing(true);
    try {
      await api<{ queued: number }>(`/api/orders/${orderId}/purchase`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      showToast('success', `Purchase started — ${purchasable} domain(s) queued`);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Purchase failed';
      showToast('error', msg);
    } finally {
      setPurchasing(false);
    }
  };

  const saveDomainEdit = async (domainId: string): Promise<void> => {
    try {
      await api(`/api/orders/${orderId}/domains/${domainId}`, {
        method: 'PATCH',
        body: JSON.stringify({ domain: editValue.trim().toLowerCase() }),
      });
      setEditingId(null);
      setEditValue('');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Update failed';
      showToast('error', msg);
    }
  };

  const cancelOrder = async (): Promise<void> => {
    if (!detail) return;
    if (!confirm('Cancel this order? The plan stays in history but no purchase will happen.')) return;
    try {
      await api(`/api/orders/${orderId}/cancel`, { method: 'POST', body: JSON.stringify({}) });
      showToast('success', 'Order cancelled');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Cancel failed');
    }
  };

  /** Toggle provisioning-verify polling for the current order — Stop / Start /
   * Restart depending on current state. Backend distinguishes resume vs
   * restart via the order's provisioning_status. */
  const toggleProvisioning = async (): Promise<void> => {
    if (!detail) return;
    const isActive = detail.order.provisioning_polling_active;
    const path = isActive
      ? `/api/orders/${orderId}/provisioning/stop`
      : `/api/orders/${orderId}/provisioning/start`;
    try {
      await api(path, { method: 'POST', body: JSON.stringify({}) });
      showToast('success', isActive ? 'Polling stopped' : 'Polling started');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Provisioning toggle failed');
    }
  };

  /** Configure all panels on this order. Idempotent; already-done panels skip. */
  const configureAllPanels = async (): Promise<void> => {
    if (!detail) return;
    if (!confirm(`Configure all ${detail.domains.length} panel(s) for this order? This runs the 11-phase orchestrator with a 20s start stagger between panels. Already-complete panels skip.`)) return;
    setConfiguring(true);
    try {
      await api(`/api/orders/${orderId}/configure`, { method: 'POST', body: JSON.stringify({}) });
      showToast('success', `Configuration queued — UI will live-update as phases complete`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Configure failed');
    } finally {
      setConfiguring(false);
    }
  };

  /** Configure a single panel by panels.id. */
  const configureSinglePanel = async (panelId: string): Promise<void> => {
    setConfiguring(true);
    try {
      await api(`/api/panels/${panelId}/configure`, { method: 'POST', body: JSON.stringify({}) });
      showToast('success', 'Panel configuration queued');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Configure failed');
    } finally {
      setConfiguring(false);
    }
  };

  /** Push one panel's credentials to 1Password (inline call). On success the
   *  panel's pushed_to_1p_at is set so the row re-renders with the badge. */
  const pushPanelToOnePw = async (panelId: string): Promise<void> => {
    setPushingPanelId(panelId);
    try {
      const result = await api<{ mode: string; itemId?: string }>(
        `/api/panels/${panelId}/onepw-push`,
        { method: 'POST' },
      );
      const verb = result.mode === 'created' ? 'pushed' : result.mode === 'skip' ? 'already in 1P' : result.mode;
      showToast('success', `Panel ${verb}`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : '1P push failed');
    } finally {
      setPushingPanelId(null);
    }
  };

  /** Push one panel's credentials to the internal-tools API (inline call,
   *  single-panel JSON body). On success the row's pushed_to_internal_tools_at
   *  is set so the row re-renders with the "in IT" badge. */
  const pushPanelToInternalTools = async (panelId: string): Promise<void> => {
    setPushingITPanelId(panelId);
    try {
      await api<{ mode: string; email: string }>(
        `/api/panels/${panelId}/internal-tools-push`,
        { method: 'POST' },
      );
      showToast('success', 'Panel pushed to internal-tools');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Internal-tools push failed');
    } finally {
      setPushingITPanelId(null);
    }
  };

  /** Run DWD + license check across every panel in the order. Persists results. */
  const checkAllStatuses = async (): Promise<void> => {
    setCheckingStatuses(true);
    try {
      await api(`/api/orders/${orderId}/check-statuses`, { method: 'POST', body: JSON.stringify({}) });
      showToast('success', 'Status check queued — refresh in ~5s for results');
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Status check failed');
    } finally {
      setCheckingStatuses(false);
    }
  };

  /** Download a CSV via the API endpoint. The browser handles the file save. */
  const downloadCsv = (path: string, fallbackName: string): void => {
    const a = document.createElement('a');
    a.href = path;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Reseller export: TSV to clipboard (paste into Sheets) or CSV download.
  // Format per row: date \t domain \t Admin \t Account \t admin \t capacity \t password
  const exportForReseller = async (mode: 'copy' | 'csv'): Promise<void> => {
    try {
      const data = await api<{ rows: string[][]; filename: string }>(
        `/api/orders/${orderId}/export`,
      );
      if (mode === 'copy') {
        const tsv = data.rows.map((r) => r.join('\t')).join('\n');
        await navigator.clipboard.writeText(tsv);
        showToast('success', `Copied ${data.rows.length} rows`);
      } else {
        const escape = (v: string): string => `"${v.replace(/"/g, '""')}"`;
        const csv = data.rows.map((r) => r.map(escape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.filename}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Export failed';
      showToast('error', msg);
    }
  };

  if (!detail) {
    return (
      <div className="ord-root">
        <div className="ord-scroll">
          <header className="ord-header">
            <button type="button" className="ord-back" onClick={onBack}>
              <ChevronIcon /><span>Back to orders</span>
            </button>
          </header>
          <div className="ord-empty">Loading…</div>
        </div>
        {toast && <div className={`ord-toast ord-toast--${toast.type}`}>{toast.message}</div>}
      </div>
    );
  }

  const { order, domains, panels } = detail;
  const panelByEmail = new Map(panels.map((p) => [p.email, p]));
  const derivedStatus = derivedOrderStatus(order, panels, domains.length);
  const allAvailable = domains.length > 0 && domains.every((d) => d.availability_status === 'available');

  // Failed rows can be retried — the processor is idempotent (skips already-
  // purchased rows, skips already-zoned rows, only re-runs the failed step).
  const failedCount = domains.filter((d) => d.purchase_status === 'failed').length;
  const hasUnstartedRows = domains.some(
    (d) =>
      (d.availability_status === 'available' || d.availability_status === 'premium') &&
      (!d.purchased_at || !d.cloudflare_zone_id || !d.nameservers_set_at) &&
      d.purchase_status !== 'failed',
  );
  // 'purchased' status is NOT terminal when there are still failed/unstarted
  // rows to retry — partial-success orders also land in 'purchased'. cantAct
  // gates secondary actions (cancel, per-row delete UI); the Retry button has
  // its own narrower disabled condition below.
  const cantAct =
    order.status === 'cancelled' ||
    (order.status === 'purchased' && failedCount === 0 && !hasUnstartedRows);

  const counts = domains.reduce(
    (acc, d) => {
      acc[d.availability_status] = (acc[d.availability_status] ?? 0) + 1;
      return acc;
    },
    {} as Record<DomainStatus, number>,
  );

  return (
    <div className="ord-root">
      <div className="ord-scroll">
        <header className="ord-header">
          <button type="button" className="ord-back" onClick={onBack}>
            <ChevronIcon /><span>Back to orders</span>
          </button>
        </header>

        <section className="ord-summary-card">
          <div className="ord-summary-top">
            <div>
              <h2 className="ord-summary-title">
                <span className="ord-mono">{order.domain_base}*{order.domain_tld}</span>
                <span className="ord-summary-sub"> · {order.panel_count} panels · {order.capacity} capacity</span>
              </h2>
              <div className="ord-summary-meta">
                Triggered {order.triggered_by ? <>by <strong>{order.triggered_by}</strong></> : null} ·{' '}
                {relativeTime(order.created_at)}
                {order.notes && <> · {order.notes}</>}
              </div>
            </div>
            <span className={`ord-badge ord-badge--${derivedStatus}`}>{derivedStatusLabel(derivedStatus)}</span>
          </div>

          {order.error_message && (
            <div className="ord-summary-error">{order.error_message}</div>
          )}

          <div className="ord-summary-grid">
            <SummaryStat label="Total" value={domains.length} />
            <SummaryStat label="Available" value={counts.available ?? 0} variant="sage" />
            <SummaryStat label="Unavailable" value={counts.unavailable ?? 0} variant={(counts.unavailable ?? 0) > 0 ? 'rose' : 'muted'} />
            <SummaryStat label="Premium" value={counts.premium ?? 0} variant={(counts.premium ?? 0) > 0 ? 'honey' : 'muted'} />
            <SummaryStat
              label="Purchased"
              value={`${domains.filter((d) => d.purchased_at).length}/${domains.length}`}
              variant={domains.every((d) => d.purchased_at) && domains.length > 0 ? 'sage' : 'muted'}
            />
            <SummaryStat label="Errored" value={counts.error ?? 0} variant={(counts.error ?? 0) > 0 ? 'rose' : 'muted'} />
          </div>

          <div className="ord-summary-actions">
            <button
              type="button"
              className="ord-btn ord-btn--primary"
              onClick={() => void checkAvailability()}
              disabled={checking || cantAct}
            >
              {checking ? 'Checking…' : 'Check availability'}
            </button>
            <button
              type="button"
              className="ord-btn ord-btn--primary"
              onClick={() => void purchase()}
              disabled={
                purchasing ||
                cantAct ||
                order.status === 'purchasing' ||
                domains.every((d) => d.availability_status !== 'available' && d.availability_status !== 'premium')
              }
              title={
                failedCount > 0
                  ? `Re-run pipeline. Already-purchased rows are skipped; only the failed step (${failedCount} row${failedCount === 1 ? '' : 's'}) is retried.`
                  : 'Purchase on Spaceship → add zones to Cloudflare → set Spaceship nameservers'
              }
            >
              {order.status === 'purchasing'
                ? 'Purchasing…'
                : purchasing
                  ? 'Starting…'
                  : failedCount > 0
                    ? `Retry failed (${failedCount}${hasUnstartedRows ? `+${domains.filter((d) => (d.availability_status === 'available' || d.availability_status === 'premium') && (!d.purchased_at || !d.cloudflare_zone_id || !d.nameservers_set_at) && d.purchase_status !== 'failed').length}` : ''})`
                    : 'Purchase on Spaceship'}
            </button>
            {!cantAct && (
              <button
                type="button"
                className="ord-btn ord-btn--ghost"
                onClick={() => void cancelOrder()}
              >
                Cancel order
              </button>
            )}
            <button
              type="button"
              className="ord-btn ord-btn--ghost"
              onClick={() => void exportForReseller('copy')}
              title="Copy TSV rows for the reseller (paste into a spreadsheet)"
            >
              Copy for reseller
            </button>
            <button
              type="button"
              className="ord-btn ord-btn--ghost"
              onClick={() => void exportForReseller('csv')}
              title="Download a CSV with the reseller-export rows"
            >
              Reseller CSV
            </button>
            {allAvailable && (
              <span className="ord-summary-ok">All {domains.length} domains available</span>
            )}
          </div>

          {order.status === 'purchased' && (
            <OrderProvisioningSection
              order={order}
              panelsProvisionedCount={detail.domains.filter((d) => d.provisioned_at !== null).length}
              onAction={toggleProvisioning}
            />
          )}

          {order.status === 'purchased' && <ResellerPanel orderId={order.id} />}
          {order.status === 'purchased' && <OnePwPanel orderId={order.id} />}
          {order.status === 'purchased' && <InternalToolsPanel orderId={order.id} />}

          {order.status === 'purchased' && (
            <div className="ord-summary-actions" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                className="ord-btn ord-btn--primary"
                onClick={() => void configureAllPanels()}
                disabled={configuring}
                title="Run the 11-phase orchestrator on every panel in this order (20s stagger, idempotent)"
              >
                {configuring ? 'Queueing…' : 'Configure all panels'}
              </button>
              <button
                type="button"
                className="ord-btn ord-btn--secondary"
                onClick={() => void checkAllStatuses()}
                disabled={checkingStatuses}
                title="Verify DWD + admin@ has no Workspace license, on every panel (API only)"
              >
                {checkingStatuses ? 'Checking…' : 'Check all statuses'}
              </button>
              <button
                type="button"
                className="ord-btn ord-btn--ghost"
                onClick={() => downloadCsv(`/api/orders/${orderId}/csv/1password`, '1password.csv')}
                title="Download all panels' final credentials in 1Password import format"
              >
                Download 1Password CSV
              </button>
              <button
                type="button"
                className="ord-btn ord-btn--ghost"
                onClick={() => downloadCsv(`/api/orders/${orderId}/csv/internal`, 'internal-tools.csv')}
                title="Download all panels' final credentials in the internal-tools format"
              >
                Download internal CSV
              </button>
              <button
                type="button"
                className="ord-btn ord-btn--ghost"
                onClick={() => setShowPhaseRef(true)}
                title="What does each pipeline phase do?"
              >
                Pipeline reference
              </button>
            </div>
          )}
        </section>

        {showPhaseRef && <PhaseReferenceModal onClose={() => setShowPhaseRef(false)} />}

        <section className="ord-card">
          <div className="ord-card-header">
            <h2 className="ord-card-title">Domains ({domains.length})</h2>
          </div>
          <table className="ord-table">
            <thead>
              <tr>
                <th className="ord-col-pos">#</th>
                <th>ID</th>
                <th>Domain</th>
                <th>Available</th>
                <th>Pipeline</th>
                <th>Config</th>
                <th>Health</th>
                <th>Note</th>
                <th className="ord-col-actions" />
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => {
                const panel = panelByEmail.get(`admin@${d.domain}`);
                const status = panelStatus(panel);
                const orderPurchased = order.status === 'purchased';
                return (
                <tr key={d.id}>
                  <td className="ord-col-pos ord-cell-faint">{d.position + 1}</td>
                  <td className="ord-cell-mono">{d.reseller_id}</td>
                  <td className="ord-cell-mono">
                    {editingId === d.id ? (
                      <input
                        className="ord-input ord-input-mono"
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveDomainEdit(d.id);
                          if (e.key === 'Escape') {
                            setEditingId(null);
                            setEditValue('');
                          }
                        }}
                      />
                    ) : (
                      <>
                        <div>{d.domain}</div>
                        {panel?.organization_name && (
                          <div className="ord-cell-faint" style={{ fontSize: 11 }}>
                            {panel.organization_name}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`ord-badge ord-badge--${d.availability_status}`}>
                      {d.availability_status}
                    </span>
                  </td>
                  <td>
                    <PipelineCell row={d} />
                  </td>
                  <td>
                    <ConfigStatusCell status={status} />
                  </td>
                  <td>
                    <HealthCell panel={panel} />
                  </td>
                  <td className="ord-cell-faint ord-cell-detail">
                    {panel?.config_error
                      ? <span style={{ color: 'var(--rose)' }}>{panel.config_error.slice(0, 60)}</span>
                      : (d.purchase_message ?? d.availability_message ?? '—')}
                  </td>
                  <td className="ord-col-actions">
                    {editingId === d.id ? (
                      <div className="ord-row-actions">
                        <button
                          type="button"
                          className="ord-btn ord-btn--primary ord-btn--xs"
                          onClick={() => void saveDomainEdit(d.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="ord-btn ord-btn--ghost ord-btn--xs"
                          onClick={() => {
                            setEditingId(null);
                            setEditValue('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="ord-row-actions">
                        {orderPurchased && status.state !== 'done' && status.state !== 'running' && status.state !== 'queued' && panel && (
                          <button
                            type="button"
                            className="ord-btn ord-btn--secondary ord-btn--xs"
                            onClick={() => void configureSinglePanel(panel.id)}
                            disabled={configuring}
                            title="Configure this single panel (idempotent)"
                          >
                            Configure
                          </button>
                        )}
                        {orderPurchased && status.state === 'done' && panel && (
                          <>
                            <button
                              type="button"
                              className="ord-btn ord-btn--ghost ord-btn--xs"
                              onClick={() => downloadCsv(`/api/panels/${panel.id}/csv/1password`, `${d.reseller_id}-1pw.csv`)}
                              title="1Password CSV for this panel"
                            >
                              1Password
                            </button>
                            <button
                              type="button"
                              className="ord-btn ord-btn--ghost ord-btn--xs"
                              onClick={() => downloadCsv(`/api/panels/${panel.id}/csv/internal`, `${d.reseller_id}-internal.csv`)}
                              title="Internal-tools CSV for this panel"
                            >
                              Internal
                            </button>
                            {panel.pushed_to_reseller_at && (
                              <span className="ord-badge ord-badge--pushed" title="Pushed to reseller (sheet + Telegram)">
                                ✓ to reseller
                              </span>
                            )}
                            {panel.pushed_to_1p_at ? (
                              <span className="ord-badge ord-badge--pushed" title="Pushed to 1Password">
                                ✓ in 1P
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="ord-btn ord-btn--secondary ord-btn--xs"
                                onClick={() => void pushPanelToOnePw(panel.id)}
                                disabled={pushingPanelId !== null}
                                title="Push this panel's credentials to 1Password"
                              >
                                {pushingPanelId === panel.id ? '…' : 'Push to 1P'}
                              </button>
                            )}
                            {panel.pushed_to_internal_tools_at ? (
                              <span className="ord-badge ord-badge--pushed" title="Pushed to internal-tools">
                                ✓ in IT
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="ord-btn ord-btn--secondary ord-btn--xs"
                                onClick={() => void pushPanelToInternalTools(panel.id)}
                                disabled={pushingITPanelId !== null}
                                title="Push this panel's credentials to internal-tools"
                              >
                                {pushingITPanelId === panel.id ? '…' : 'Push to IT'}
                              </button>
                            )}
                          </>
                        )}
                        {!cantAct && d.availability_status !== 'available' && (
                          <button
                            type="button"
                            className="ord-btn ord-btn--secondary ord-btn--xs"
                            onClick={() => {
                              setEditingId(d.id);
                              setEditValue(d.domain);
                            }}
                            title="Edit domain — useful when this one's taken"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </section>
      </div>

      {toast && <div className={`ord-toast ord-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Per-row config status indicator ─────────────────────── */

function ConfigStatusCell({ status }: { status: PanelStatus }): JSX.Element {
  if (status.state === 'not-started') {
    return <span className="ord-cell-faint">—</span>;
  }
  if (status.state === 'done') {
    return (
      <span className="ord-badge ord-badge--purchased" title="All 11 phases complete">
        ✓ done
      </span>
    );
  }
  if (status.state === 'failed') {
    return (
      <span className="ord-badge ord-badge--failed" title="Configuration error — see Note">
        ✕ failed · {status.completedPhases}/{status.totalPhases}
      </span>
    );
  }
  if (status.state === 'queued') {
    return (
      <span className="ord-badge ord-badge--pending" title="Queued, no phases started yet">
        queued
      </span>
    );
  }
  // running
  const next = status.nextPhase ? phaseLabel(status.nextPhase) : '';
  return (
    <span className="ord-badge ord-badge--purchasing" title={`Currently on: ${next}`}>
      · {status.completedPhases}/{status.totalPhases} {next ? `· ${next}` : ''}
    </span>
  );
}

function PhaseReferenceModal({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <div className="ord-modal-overlay" onClick={onClose}>
      <div className="ord-modal ord-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="ord-modal-header">
          <h3 className="ord-modal-title">Panel-config pipeline reference</h3>
          <button type="button" className="ord-icon-btn" onClick={onClose} title="Close">
            <XIcon />
          </button>
        </div>
        <div className="ord-modal-body">
          <p className="ord-cell-faint" style={{ marginBottom: 14 }}>
            Each panel runs through these 11 phases. Every phase is idempotent — re-running on a panel only touches phases not yet complete. The "Configure all panels" button orchestrates them with a 20s stagger between panels.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PHASE_REFERENCE.map((ph) => (
              <div key={ph.num} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div
                  style={{
                    flex: '0 0 28px',
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    background: 'var(--surface-warm)',
                    color: 'var(--ink-muted)',
                    fontWeight: 600,
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {ph.num}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 3 }}>{ph.name}</div>
                  <div className="ord-cell-faint" style={{ fontSize: 13, lineHeight: 1.5 }}>{ph.what}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthCell({ panel }: { panel: PanelConfigState | undefined }): JSX.Element {
  if (!panel || !panel.health_checked_at) {
    return <span className="ord-cell-faint">—</span>;
  }
  const dwd = panel.health_dwd_ok;
  const lic = panel.health_license_cleared;
  const okBoth = dwd === true && lic === true;
  const variant = okBoth ? 'available' : 'error';
  const label = okBoth
    ? `DWD ✓ · License clear`
    : `${dwd ? 'DWD ✓' : 'DWD ✕'} · ${lic ? 'License clear' : 'License attached'}`;
  return (
    <span
      className={`ord-badge ord-badge--${variant}`}
      title={panel.health_error ?? `Last checked ${relativeTime(panel.health_checked_at)}`}
    >
      {label}
    </span>
  );
}

function SummaryStat({
  label,
  value,
  variant = 'default',
}: {
  label: string;
  value: number | string;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted';
}): JSX.Element {
  return (
    <div className="ord-summary-stat">
      <span className="ord-summary-stat-label">{label}</span>
      <span className={`ord-summary-stat-value ord-summary-stat-value--${variant}`}>{value}</span>
    </div>
  );
}

/* ─── Pipeline cell — three-step indicator per row ───────── */

type StepState = 'idle' | 'busy' | 'done' | 'fail';

function stepClass(state: StepState): string {
  return `ord-step ord-step--${state}`;
}

function PipelineCell({ row }: { row: OrderDomain }): JSX.Element {
  const ps = row.purchase_status;

  // Step 1: Purchase
  let purchaseState: StepState = 'idle';
  if (row.purchased_at) purchaseState = 'done';
  else if (ps === 'purchasing') purchaseState = 'busy';
  else if (ps === 'failed' && (row.purchase_message ?? '').startsWith('Purchase')) purchaseState = 'fail';

  // Step 2: Cloudflare zone
  let zoneState: StepState = 'idle';
  if (row.cloudflare_zone_id) zoneState = 'done';
  else if (purchaseState === 'done' && ps === 'purchasing') zoneState = 'busy';
  else if (ps === 'failed' && (row.purchase_message ?? '').startsWith('Zone')) zoneState = 'fail';

  // Step 3: Nameservers
  let nsState: StepState = 'idle';
  if (row.nameservers_set_at) nsState = 'done';
  else if (zoneState === 'done' && ps === 'purchasing') nsState = 'busy';
  else if (ps === 'failed' && (row.purchase_message ?? '').startsWith('NS')) nsState = 'fail';

  // If nothing's started yet, show a single dash to keep the column quiet
  if (purchaseState === 'idle' && zoneState === 'idle' && nsState === 'idle' && ps === null) {
    return <span className="ord-cell-faint">—</span>;
  }

  return (
    <div className="ord-pipeline">
      <div className={stepClass(purchaseState)} title="Purchase on Spaceship">
        <span className="ord-step-icon">{stepGlyph(purchaseState)}</span>
        <span className="ord-step-label">Buy</span>
      </div>
      <span className="ord-step-sep">›</span>
      <div className={stepClass(zoneState)} title="Cloudflare zone">
        <span className="ord-step-icon">{stepGlyph(zoneState)}</span>
        <span className="ord-step-label">CF zone</span>
      </div>
      <span className="ord-step-sep">›</span>
      <div className={stepClass(nsState)} title="Spaceship nameservers updated">
        <span className="ord-step-icon">{stepGlyph(nsState)}</span>
        <span className="ord-step-label">NS</span>
      </div>
    </div>
  );
}

function stepGlyph(state: StepState): string {
  switch (state) {
    case 'done': return '✓';
    case 'busy': return '·';
    case 'fail': return '✕';
    default:     return '○';
  }
}
